import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { supabase } from '../utils/supabaseClient';
import type { TableName } from '../features/quality-check/workflow/TableSection';

export interface RunQcTableRequest {
  program_id: string;
  baci_code: string;
  seed_url: string;
  organization?: string;
  custom_prompt?: string;
}

export interface AISuggestion {
  field: string;
  currentValue: string | null;
  suggestedValue: string | null;
  source?: string;
}

export interface RunQcTableResponse {
  status: 'completed' | 'failed';
  message: string;
  suggestions?: AISuggestion[];
  qc_flag_suggestion?: boolean;
  qc_comment_suggestion?: string;
  report_path?: string;
  error?: string;
  agent_response?: string;
}

/**
 * Invoke a QC edge function for any table to trigger automated verification
 *
 * @param tableName - The table to run QC on (contacts, deadlines, requirements, etc.)
 * @param options - Optional callbacks and configuration
 *
 * @example
 * ```typescript
 * const runQc = useRunQcTable('contacts', {
 *   onSuccess: (data) => {
 *     console.log('QC completed:', data.message);
 *   }
 * });
 *
 * runQc.mutate({
 *   program_id: '123',
 *   baci_code: 'AC2',
 *   seed_url: 'https://example.com'
 * });
 * ```
 */
async function runQcTableMutation(
  tableName: TableName,
  request: RunQcTableRequest
): Promise<RunQcTableResponse> {
  if (!request.program_id) {
    throw new Error('program_id is required');
  }

  if (!request.baci_code) {
    throw new Error('baci_code is required');
  }

  if (!request.seed_url) {
    throw new Error('seed_url is required');
  }

  const edgeFunctionName = `qc-run-${tableName}`;

  console.log(`[QC ${tableName}] Invoking edge function with request:`, {
    program_id: request.program_id,
    baci_code: request.baci_code,
    has_seed_url: !!request.seed_url,
    seed_url_preview: request.seed_url?.substring(0, 50),
    edge_function: edgeFunctionName,
    custom_prompt_active: Boolean(request.custom_prompt),
    custom_prompt_chars: request.custom_prompt?.length ?? 0
  });

  const { data, error } = await supabase.functions.invoke<RunQcTableResponse>(
    edgeFunctionName,
    {
      body: request
    }
  );

  console.log(`[QC ${tableName}] Response:`, { data, error });

  if (error) {
    const errorDetails = {
      message: error.message,
      name: error.name,
      context: (error as any).context,
      status: (error as any).status
    };
    console.error(`[QC ${tableName}] Edge function error:`, errorDetails);

    // Try to extract response body for better error details
    if ((error as any).context instanceof Response) {
      try {
        const responseBody = await (error as any).context.clone().text();
        console.error(`[QC ${tableName}] Response body:`, responseBody);
        throw new Error(`Edge Function Error: ${error.message}\n\nResponse: ${responseBody}`);
      } catch (readError) {
        console.error(`[QC ${tableName}] Could not read response body:`, readError);
      }
    }

    throw new Error(`Edge Function Error (${edgeFunctionName}): ${error.message || 'Failed to invoke QC verification'}`);
  }

  if (!data) {
    throw new Error(`No response from QC verification (${edgeFunctionName})`);
  }

  if (data.status === 'failed') {
    throw new Error(data.error || data.message || `QC verification failed (${edgeFunctionName})`);
  }

  return data;
}

/**
 * React Query mutation hook to run QC verification on any table
 * Automatically refetches relevant data after successful verification
 *
 * @param tableName - The table to run QC on
 * @param options - Optional callbacks (onSuccess, onError) and mutation config
 *
 * @returns Mutation hook with mutate/mutateAsync methods
 */
export function useRunQcTable(
  tableName: TableName,
  options?: {
    onSuccess?: (data: RunQcTableResponse) => void;
    onError?: (error: Error) => void;
  }
): UseMutationResult<RunQcTableResponse, Error, RunQcTableRequest, unknown> {
  const queryClient = useQueryClient();

  return useMutation<RunQcTableResponse, Error, RunQcTableRequest, unknown>({
    mutationFn: (request) => runQcTableMutation(tableName, request),
    onSuccess: (data, variables) => {
      // Invalidate report card query (comprehensive data)
      queryClient.invalidateQueries({
        queryKey: ['program-report-card', variables.baci_code]
      });

      // Invalidate table-specific query if it exists
      queryClient.invalidateQueries({
        queryKey: [`program-${tableName}`, variables.program_id]
      });

      // Invalidate activity logs
      queryClient.invalidateQueries({
        queryKey: ['activity-log', variables.program_id]
      });

      // Call user's onSuccess callback if provided
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      console.error(`[QC ${tableName}] Mutation error:`, error);

      // Call user's onError callback if provided
      options?.onError?.(error);
    }
  });
}

export default useRunQcTable;
